import React, { ComponentType, ReactNode } from 'react';

import classNames from 'classnames';

import KarmaStarColoredIcon from '@uikit/icons/KarmaStarColored';

import {
  REWARD_POOL_CURRENCY,
  REWARD_POOL_DISTRIBUTION_POLICY,
  RewardPool,
} from '@shared/api/types/rewardPools';
import { MsqLogo } from '@shared/constants/icons';
import { pluralizeWord } from '@shared/helpers/formatters';
import { formatAmountDisplay } from '@shared/helpers/number';

export const getRewardPoolRewards = ({
  rewardPool,
  selfReward,
}: {
  rewardPool: RewardPool;
  selfReward?: number | null;
}) => {
  const rewards: {
    id: string;
    reward: string;
    rewardPerWinner?: ReactNode;
    selfReward: string;
    Icon?: ComponentType<{ className?: string }>;
    bgClassName: string;
    borderColorClassName: string;
  }[] = [];

  const rewardCurrency = {
    [REWARD_POOL_CURRENCY.KARMA]: 'Karma',
    [REWARD_POOL_CURRENCY.SQR]: 'SQR',
    [REWARD_POOL_CURRENCY.USDT]: 'USDT',
  }[rewardPool.reward_currency];
  const formattedSelfReward = `${formatAmountDisplay(selfReward || 0)} ${rewardCurrency}`;
  const formattedReward = `${formatAmountDisplay(rewardPool.reward_amount || 0)} ${rewardCurrency}`;
  const rewardPerWinner =
    rewardPool.distribution_policy === REWARD_POOL_DISTRIBUTION_POLICY.RAFFLE &&
    rewardPool.number_of_winners ? (
      <>
        {formatAmountDisplay(rewardPool.reward_amount / rewardPool.number_of_winners)}{' '}
        {rewardCurrency} × {rewardPool.number_of_winners}{' '}
        {pluralizeWord('Winner', rewardPool.number_of_winners)}
      </>
    ) : null;

  if (rewardPool.reward_currency === REWARD_POOL_CURRENCY.SQR) {
    rewards.push({
      id: 'sqr',
      reward: formattedReward,
      selfReward: formattedSelfReward,
      rewardPerWinner: rewardPerWinner,
      Icon: MsqLogo,
      bgClassName:
        'bg-[linear-gradient(206.57deg,rgba(255,128,102,0.16)_0%,rgba(229,102,255,0.16)_41.67%,rgba(102,178,255,0.16)_83.33%)]',
      borderColorClassName: 'border-[#FADBEB]',
    });
  } else if (rewardPool.reward_currency === REWARD_POOL_CURRENCY.USDT) {
    rewards.push({
      id: 'usdt',
      reward: formattedReward,
      selfReward: formattedSelfReward,
      rewardPerWinner: rewardPerWinner,
      bgClassName: 'bg-[linear-gradient(225deg,#ECF2FD_0%,#F6F9FE_50%,#ECF2FD_100%)]',
      borderColorClassName: 'border-[#DAE5FB]',
    });
  } else if (rewardPool.reward_currency === REWARD_POOL_CURRENCY.KARMA) {
    rewards.push({
      id: 'karma',
      reward: formattedReward,
      selfReward: formattedSelfReward,
      rewardPerWinner: rewardPerWinner,
      Icon: KarmaStarColoredIcon,
      bgClassName: 'bg-[linear-gradient(225deg,#FCEDF5_0%,#FEF6FA_50%,#FCEDF5_100%)]',
      borderColorClassName: 'border-[#FADBEB]',
    });
  }

  return rewards;
};

interface RewardPoolsCardProps {
  rewardPool: RewardPool;
}

const RewardPoolCardRewards = ({ rewardPool }: RewardPoolsCardProps) => {
  const rewards = getRewardPoolRewards({ rewardPool });

  return (
    <ul
      className={classNames('flex gap-1.5 mx-auto font-semibold whitespace-nowrap text-[#09090B]', {
        'max-xl:text-sm': rewards.length > 1,
      })}
    >
      {rewards.map(({ id, reward, Icon }) => {
        return (
          <li
            key={id}
            className="bg-neutral-50 gap-1.5 min-h-10 px-4 inline-flex items-center justify-center text-center rounded-full py-1"
            data-qa="reward_amount"
          >
            {Icon && <Icon className="icon-xs" />}
            {reward}
          </li>
        );
      })}
    </ul>
  );
};

export default RewardPoolCardRewards;
